/**
 * Audio (TRD-04 §9).
 *
 * The playback backend is behind `AudioSink` so the parts that actually carry risk —
 * voice limiting and pitch variance — are unit-testable without a browser. A wave-25
 * board fires roughly 40 shots a second; without limiting that is not "loud", it is
 * literal white noise, and it is the difference between audio that helps and audio the
 * player mutes in the first minute.
 */

export type SfxId =
  | 'shoot_physical'
  | 'shoot_magic'
  | 'shoot_chain'
  | 'impact'
  /** An AREA hit, which is a different event from a bullet landing on one enemy. */
  | 'explosion'
  | 'crit'
  | 'enemy_die_small'
  | 'enemy_die_large'
  | 'boss_die'
  | 'core_hit'
  | 'place_tower'
  | 'sell_tower'
  | 'buy'
  | 'reroll'
  | 'merge'
  | 'synergy_proc'
  | 'wave_start'
  | 'wave_clear'
  | 'run_over'
  | 'ui_hover'
  | 'ui_click';

export interface PlayOptions {
  rate: number;
  volume: number;
}

/** Whatever actually makes noise. Howler in the browser, a spy in tests. */
export interface AudioSink {
  play(id: SfxId, options: PlayOptions): number | null;
  stop(handle: number): void;
}

export interface AudioSettings {
  master: number;
  sfx: number;
  music: number;
}

/** R-04-30 — browser games that open at full volume get closed. */
export const DEFAULT_AUDIO_SETTINGS: AudioSettings = { master: 0.6, sfx: 1, music: 0.7 };

const MAX_PER_SOUND = 4;

/**
 * Sounds that must not stack, and how many of them may run at once.
 *
 * The default of four overlapping copies is right for a shot that is a short percussive
 * hit — four bullets in the air sound like four bullets. It is wrong for a sustained
 * tone: a chain arc is a continuous electrical sound with a long tail, and four of them
 * layered is not four shots, it is a drone that never stops and buries everything else.
 * At a limit of one, each new arc cuts the last one off, which is what a repeating
 * electrical weapon actually sounds like.
 */
const VOICE_LIMIT: Partial<Record<SfxId, number>> = {
  shoot_chain: 1,
};
const MAX_TOTAL_VOICES = 24;
/** R-04-27 — identical repeated samples are the fastest way to make a game feel cheap. */
const PITCH_VARIANCE = 0.16;

interface Voice {
  id: SfxId;
  handle: number;
  startedAt: number;
}

export class AudioBus {
  settings: AudioSettings = { ...DEFAULT_AUDIO_SETTINGS };

  private voices: Voice[] = [];
  private clock = 0;

  constructor(private sink: AudioSink) {}

  /**
   * Swap the backend once real audio boots.
   *
   * The bus is built long before an AudioContext is allowed to exist — browsers demand a
   * user gesture — so it starts on `SilentSink` and the game is fully playable before
   * this is ever called. Live voices are dropped rather than carried across: their
   * handles belong to the OLD sink, and asking the new one to stop them would either do
   * nothing or stop an unrelated sound that happened to be given the same number.
   */
  setSink(sink: AudioSink): void {
    this.clear();
    this.sink = sink;
  }

  /** Advance the internal clock. Driven by real elapsed time, like the rest of VFX. */
  update(deltaMs: number): void {
    this.clock += deltaMs;
  }

  play(id: SfxId, volumeScale = 1): void {
    const volume = this.settings.master * this.settings.sfx * volumeScale;
    if (volume <= 0) return;

    // Per-sound cap first: forty simultaneous `shoot_physical` is the actual failure
    // mode, not forty different sounds. Evicting the OLDEST is what makes a limit of one
    // read as "retrigger" rather than "drop the new one".
    const limit = VOICE_LIMIT[id] ?? MAX_PER_SOUND;
    let sameId = this.voices.filter((v) => v.id === id);
    while (sameId.length >= limit) {
      const oldest = sameId.reduce((a, b) => (a.startedAt <= b.startedAt ? a : b));
      this.stopVoice(oldest);
      sameId = sameId.filter((v) => v !== oldest);
    }

    if (this.voices.length >= MAX_TOTAL_VOICES) {
      const oldest = this.voices.reduce((a, b) => (a.startedAt <= b.startedAt ? a : b));
      this.stopVoice(oldest);
    }

    const rate = 1 + (Math.random() - 0.5) * PITCH_VARIANCE;
    const handle = this.sink.play(id, { rate, volume });
    if (handle === null) return;

    this.voices.push({ id, handle, startedAt: this.clock });
  }

  /** Called by the sink when a sound finishes on its own. */
  release(handle: number): void {
    this.voices = this.voices.filter((v) => v.handle !== handle);
  }

  private stopVoice(voice: Voice): void {
    this.sink.stop(voice.handle);
    this.voices = this.voices.filter((v) => v !== voice);
  }

  get activeVoices(): number {
    return this.voices.length;
  }

  voicesOf(id: SfxId): number {
    return this.voices.filter((v) => v.id === id).length;
  }

  clear(): void {
    for (const v of [...this.voices]) this.stopVoice(v);
  }
}

/**
 * A sink that does nothing.
 *
 * The default until real audio exists. Silence is a legitimate state — the game must be
 * fully playable without a single sound file present, so a missing asset degrades rather
 * than throwing on the first shot.
 */
export class SilentSink implements AudioSink {
  private next = 1;
  play(): number {
    return this.next++;
  }
  stop(): void {
    /* nothing to stop */
  }
}
